import {ApiUserProfile} from 'src/lib/api/auth';
import featureFlags from 'src/lib/featureFlags';

/**
 * localStorage override so the direct-read path can be force-enabled for a single
 * session (customer debugging) or killed instantly without a deploy.
 */
const OVERRIDE_KEY = 'hfw.dynamicalDirectWeather';
const SHADOW_OVERRIDE_KEY = 'hfw.dynamicalShadowCompare';

/**
 * Fraction of non-internal sessions that opt into shadow comparison. Internal
 * dogfooding and the parity sweep came back clean with no regressions, so we
 * widen to a small slice of real customer traffic to measure production
 * accuracy at scale before flipping DYNAMICAL_DIRECT_WEATHER on.
 */
const SHADOW_SESSION_SAMPLE_RATE = 0.1;
const SHADOW_BUCKET_KEY = 'hfw.dynamicalShadowBucket';

function readOverride(key: string): 'on' | 'off' | null {
  try {
    const override = window.localStorage.getItem(key);
    if (override === 'on' || override === 'off') {
      return override;
    }
  } catch {
    // localStorage can throw in privacy modes; fall through to the flag.
  }
  return null;
}

export function isDynamicalDirectEnabled(profile: ApiUserProfile | null): boolean {
  const override = readOverride(OVERRIDE_KEY);
  if (override) {
    return override === 'on';
  }
  return featureFlags.DYNAMICAL_DIRECT_WEATHER(profile);
}

/**
 * Roll the current session into or out of the shadow-comparison sample once,
 * then reuse that verdict for the rest of the session. Persisting the roll keeps
 * a user's participation stable across re-renders and reloads, so the parity
 * telemetry reflects whole sessions rather than churn.
 */
function inShadowSessionSample(): boolean {
  try {
    const stored = window.sessionStorage.getItem(SHADOW_BUCKET_KEY);
    if (stored === 'in') {
      return true;
    }
    if (stored === 'out') {
      return false;
    }
    const rolledIn = Math.random() < SHADOW_SESSION_SAMPLE_RATE;
    window.sessionStorage.setItem(SHADOW_BUCKET_KEY, rolledIn ? 'in' : 'out');
    return rolledIn;
  } catch {
    // sessionStorage can throw in privacy modes; treat as out of the sample.
    return false;
  }
}

/**
 * Whether to run the background parity comparison (serve API, shadow-read
 * dynamical, record the diff). Independent kill switch from the live-read flag.
 * Internal users are always in; other sessions opt in at
 * SHADOW_SESSION_SAMPLE_RATE.
 */
export function isDynamicalShadowEnabled(profile: ApiUserProfile | null): boolean {
  const override = readOverride(SHADOW_OVERRIDE_KEY);
  if (override) {
    return override === 'on';
  }
  return featureFlags.DYNAMICAL_SHADOW_COMPARISON(profile) || inShadowSessionSample();
}
